'use client';

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square, Send, X } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Transaction } from '@/types/transaction';
import { transcribeAudio, extractTransactionDetails } from '@/lib/openai';

interface VoiceRecorderProps {
  onTranscriptionComplete: (text: string) => void;
  onCancel: () => void;
  selectedWallet: any;
}

export default function VoiceRecorder({ 
  onTranscriptionComplete, 
  onCancel,
  selectedWallet 
}: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    if (!selectedWallet) {
      toast({
        title: "Error",
        description: "Please select a wallet first before recording.",
        variant: "destructive",
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        try {
          setIsProcessing(true);
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
          console.log('Processing audio recording...');
          const transcribedText = await transcribeAudio(audioBlob);
          console.log('Transcribed text:', transcribedText);
          const result = await extractTransactionDetails(transcribedText);
          console.log('Transaction details:', result);
          
          // Handle multiple transactions
          const transactions = result.transactions || [];
          if (transactions.length > 0) {
            onTranscriptionComplete(transcribedText);
            toast({
              title: "Success",
              description: `Found ${transactions.length} transaction(s). Please review and confirm.`,
            });
          } else {
            toast({
              title: "Error",
              description: "No transactions found in the recording. Please try again.",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('Error processing voice input:', error);
          toast({
            title: "Error",
            description: "Failed to process voice input. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsProcessing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast({
        title: "Recording Started",
        description: "Click the button again to stop recording.",
      });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to access microphone. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setHasRecording(true);
      toast({
        title: "Recording Stopped",
        description: "Processing your voice input...",
      });
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button
          variant={isRecording ? "destructive" : "default"}
          size="icon"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isProcessing}
        >
          {isRecording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onCancel}
          disabled={isRecording || isProcessing}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
} 